import React from "react";
import type { ReactNode } from "react";

import { wait } from "@api";
import { getSdiraBusinessName, SdiraConnectBankAccountAlert } from "@components/Dwolla";
import {
  INVESTMENT_PROFILE_TYPE_SDIRA,
  INVESTMENT_PROFILE_TYPE_ENTITY,
} from "@components/Domain";
import { updateMyInvestmentSourcesOperation } from "@api/services/investments";
import type { Operation } from "@api";

type Source = Record<string, unknown>;

type SourceMutation =
  | { senderSourceId: string }
  | { receiverSourceId: string };

type UpdateParameters = {
  id: string;
  mutation: SourceMutation;
};

type Request = (operation: Operation, parameters: UpdateParameters) => Promise<{ data: unknown }>;

type Investment = {
  id: string;
  name?: string;
  profileType?: string;
  senderSourceId?: string;
  receiverSourceId?: string;
};

type RetryModal = {
  current: {
    open: (previousSource: Source, investmentIds: string[], callback: () => void) => void;
  };
};

type ConfirmModal = {
  current: {
    open: (onOk: () => void) => void;
  };
};

type Options = {
  isSender?: boolean;
  request: Request;
  investment: Investment;
  confirmRetryFailedTransactionsModal?: RetryModal;
  onInvestmentSourceChanged: (data: unknown, isNewSource: boolean) => unknown;
  connectBankAccountPlaidConfirmModal?: ConfirmModal;
  connectBankAccountManuallyConfirmModal?: ConfirmModal;
};

type DropdownProps = {
  onSelectSource: (previousSource: Source, callback: () => void) => void;
  connectSourceTitle?: string;
  connectSourceHeader?: ReactNode;
  onConnectPlaidSelected: (onOk: () => void) => void;
  onConnectConnectManuallySelected?: (onOk: () => void) => void;
};

export type SourceSelectProps = {
  sourceId?: string;
  onSelect: (sourceId: string, source: Source, isNewSource: boolean) => Promise<unknown>;
  businessName?: string;
  dropdownProps: DropdownProps;
  isReceiveOnly?: boolean;
};

const LABEL_SDIRA_CONNECT_SOURCE = "Connect Custodian Bank Account";

const NEW_SOURCE_CONNECT_DELAY = 250;
const NO_DELAY = 0;

const getInvestmentSourceSelectProps = ({
  isSender = false,
  request,
  investment,
  confirmRetryFailedTransactionsModal,
  onInvestmentSourceChanged,
  connectBankAccountPlaidConfirmModal,
  connectBankAccountManuallyConfirmModal,
}: Options): SourceSelectProps => {
  const {
    id: investmentId,
    name: businessName,
    profileType,
    senderSourceId,
    receiverSourceId,
  } = investment;

  const getMutation = (sourceId: string): SourceMutation => {
    const mutation = isSender
      ? { senderSourceId: sourceId }
      : { receiverSourceId: sourceId };

    return mutation;
  };

  const onSelect = (sourceId: string, _source: Source, isNewSource: boolean): Promise<unknown> => {
    const delayMs = isNewSource
      ? NEW_SOURCE_CONNECT_DELAY
      : NO_DELAY;

    const result = wait(delayMs)
      .then(() => request(updateMyInvestmentSourcesOperation, {
        id: investmentId,
        mutation: getMutation(sourceId),
      }))
      .then(({ data }) => onInvestmentSourceChanged(data, isNewSource));

    return result;
  };

  const onSelectSource = (previousSource: Source, callback: () => void): void => {
    if (!confirmRetryFailedTransactionsModal) {
      callback();

      return;
    }

    confirmRetryFailedTransactionsModal.current.open(previousSource, [ investmentId ], callback);
  };

  const onConnectPlaidSelected = (onOk: () => void): void => {
    if (!connectBankAccountPlaidConfirmModal) {
      onOk();

      return;
    }

    connectBankAccountPlaidConfirmModal.current.open(onOk);
  };

  const sourceId = isSender
    ? senderSourceId
    : receiverSourceId;

  const props: SourceSelectProps = {
    sourceId,
    onSelect,
    dropdownProps: {
      onSelectSource,
      onConnectPlaidSelected,
    },
  };

  if (connectBankAccountManuallyConfirmModal) {
    props.dropdownProps.onConnectConnectManuallySelected = (onOk: () => void): void => {
      connectBankAccountManuallyConfirmModal.current.open(onOk);
    };
  }

  const isEntity = profileType === INVESTMENT_PROFILE_TYPE_ENTITY;

  if (isEntity) {
    props.businessName = businessName;
  }

  const isSdira = profileType === INVESTMENT_PROFILE_TYPE_SDIRA;

  if (isSdira) {
    const connectSourceHeader = <SdiraConnectBankAccountAlert />;

    props.businessName = getSdiraBusinessName(businessName);
    props.isReceiveOnly = true;
    props.dropdownProps = {
      ...props.dropdownProps,
      connectSourceHeader,
      connectSourceTitle: LABEL_SDIRA_CONNECT_SOURCE,
    };
  }

  return props;
};

export default getInvestmentSourceSelectProps;
